<script lang="ts">
  import { useNavigate } from 'svelte-navigator';
  import { _ } from 'svelte-i18n';

  import { AppState, ToastQueue } from '../etc/stores';
  import { maskEmail, maskPhone } from '../etc/utils';
  import { Container } from '../services/container';
  import { Auth } from '../services/auth';

  import Page from '../components/misc/Page.svelte';
  import Title from '../components/misc/Title.svelte';
  import Link from '../components/misc/Link.svelte';
  import Button from '../components/form/Button.svelte';
  import Radio from '../components/form/Radio.svelte';
  import BackArrow from '../components/misc/BackArrow.svelte';
  import EmailIcon from '../components/icons/Email.svelte';
  import PhoneIcon from '../components/icons/Phone.svelte';
  import { Logger } from '../services/logger';
  import { extractMessageFromError } from '../services/api';
  import type { ISignInOptions } from '../etc/types';
  import { onMount } from 'svelte';
  import Analytics from '../services/analytics';

  const t = (text: string) => {
    return $_(`login_method.${text}`);
  };

  const navigate = useNavigate();
  const auth = Container.get(Auth);
  const logger = Container.get(Logger).context('LoginMethod.svelte') as Logger;

  let loading = false;
  let selected: string;

  const methodMap = {
    email: {
      maskFn: maskEmail,
      icon: EmailIcon
    },
    phone: {
      maskFn: maskPhone,
      icon: PhoneIcon
    }
  };

  const maskOptionValue = (option: ISignInOptions) =>
    methodMap[option.type].maskFn(option.value, true);

  const { signInOptions } = $AppState.auth;

  const isOptionsFromTheSameAccount =
    !signInOptions?.length ||
    signInOptions.every(
      option => option.customerId === signInOptions[0].customerId
    );

  const mountLabel = (option: ISignInOptions) => {
    if (isOptionsFromTheSameAccount) {
      return `<span style="color: var(--twism-theme-darkText);">${maskOptionValue(
        option
      )}</span>`;
    }

    return `
      ${option.name}
      <p style="font-weight: 400; color: var(--twism-theme-darkText); margin-top: 4px;">${maskOptionValue(
        option
      )}</p>
    `;
  };

  const normalizedOptions = signInOptions.map(option => ({
    ...option,
    maskedValue: maskOptionValue(option),
    icon: methodMap[option.type].icon,
    label: mountLabel(option)
  }));

  const onClick = async () => {
    const method: ISignInOptions = normalizedOptions.find(
      option => option.value === selected
    );

    if (!method) return;

    try {
      loading = true;

      const id = await auth.requestCodeTarget(method.value, method.type);

      navigate(`/code`, {
        state: {
          method: {
            ...method,
            validationCode: { id }
          },
          resumedDescription: true
        }
      });
    } catch (e) {
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      loading = false;
    }
  };

  const onLink = () => {
    navigate('/connect-email');
  };

  onMount(() => {
    Analytics.logPageView(
      `Login Method${Analytics.getFreeCoinsTitleFlow()}`,
      '/login-method'
    );
  });
</script>

<Page eventCategoryInCloseGA="signin-state">
  <BackArrow />
  <div class="loginMethod__header">
    <Title as="h1"
      >{t(
        `title.${isOptionsFromTheSameAccount ? 'same_account' : 'diff_account'}`
      )}</Title
    >
  </div>
  <div class="loginMethod__selection">
    {#each normalizedOptions as option}
      <Radio
        name="method"
        icon={option.icon}
        value={option.value}
        label={option.label}
        bind:group={selected}
      />
    {/each}
  </div>
  <div class="loginMethod__button">
    <Button
      type="button"
      on:click={onClick}
      label={t('btn_next')}
      disabled={!selected}
      {loading}
    />
  </div>
  <div class="loginMethod__link">
    <Link on:click={onLink}>{t('link_account')}</Link>
  </div>
</Page>

<style lang="scss">
  div {
    width: 100%;
  }
  .loginMethod {
    &__header {
      margin-top: 64px;
    }
    &__selection {
      margin-top: 64px;
      display: flex;
      flex-direction: column;
    }
    &__button {
      margin-top: 51px;
    }

    &__link {
      margin-top: 24px;
    }
  }
</style>
